import React from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { 
  FolderOpen, 
  UserCheck,
  Calendar,
  Users,
  Mail
} from 'lucide-react';
import { projectAPI } from '../services/api';
import { format } from 'date-fns';

const Projects = () => {
  // Fetch employee's assigned projects
  const { data: myProjects, isLoading } = useQuery(
    'my-projects',
    () => projectAPI.getMyAssignedProjects(),
    {
      select: (response) => response.data.projects || [],
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FolderOpen className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Projects</h1>
              <p className="text-gray-600 mt-1">
                View all projects you are assigned to and their managers
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-purple-600">
              {myProjects?.length || 0}
            </p>
            <p className="text-sm text-gray-500">
              {myProjects?.length === 1 ? 'Project' : 'Projects'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Projects Grid */}
      {myProjects && myProjects.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {myProjects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Project Header */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {project.name}
                    </h3>
                    {project.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {project.description}
                      </p>
                    )}
                  </div>
                  <span className={`ml-3 px-3 py-1 text-xs font-medium rounded-full ${
                    project.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {project.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Project Details */}
              <div className="px-6 py-4 space-y-4">
                {/* Created Date */}
                {project.createdAt && (
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>
                      Created on {format(new Date(project.createdAt), 'MMM dd, yyyy')}
                    </span>
                  </div>
                )}

                {/* Team Members Count */}
                {project.assignedEmployeesCount !== undefined && (
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>
                      {project.assignedEmployeesCount} team {project.assignedEmployeesCount === 1 ? 'member' : 'members'}
                    </span>
                  </div>
                )}

                {/* Divider */}
                {project.assignedManagers && project.assignedManagers.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <UserCheck className="w-5 h-5 text-purple-600" />
                      <h4 className="text-sm font-semibold text-gray-900">
                        Project Managers
                      </h4>
                    </div>

                    {/* Manager Cards */}
                    <div className="space-y-3">
                      {project.assignedManagers.map((manager) => (
                        <div
                          key={manager.id}
                          className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          {/* Manager Avatar */}
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-sm">
                              <span className="text-sm font-semibold text-white">
                                {manager.firstName?.charAt(0)}{manager.lastName?.charAt(0)}
                              </span>
                            </div>
                          </div>

                          {/* Manager Details */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {manager.firstName} {manager.lastName}
                            </p>
                            <div className="flex items-center space-x-1 text-xs text-gray-600">
                              <Mail className="w-3 h-3" />
                              <a 
                                href={`mailto:${manager.email}`}
                                className="truncate hover:text-blue-600 hover:underline"
                              >
                                {manager.email}
                              </a>
                            </div>
                          </div>

                          {/* Manager Badge */}
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Manager
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Managers Message */}
                {(!project.assignedManagers || project.assignedManagers.length === 0) && (
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-center space-x-2 py-4 text-sm text-gray-500 bg-gray-50 rounded-lg">
                      <UserCheck className="w-4 h-4" />
                      <span>No managers assigned to this project</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-sm p-12"
        >
          <div className="text-center">
            <div className="mx-auto h-24 w-24 rounded-full bg-purple-100 flex items-center justify-center mb-4">
              <FolderOpen className="h-12 w-12 text-purple-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No projects assigned yet
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              You haven't been assigned to any projects yet. Once you're added to a project by an admin or manager, it will appear here.
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Projects;

